import * as React from 'react'
import { useNavigate, useLocation, useParams, NavigateFunction } from 'react-router-dom'

/**
 * v5-style props passed by the `withRouter` HOC. Kept API-compatible with
 * react-router-dom v5's `RouteComponentProps` so legacy class/function
 * components don't need to change their prop expectations — only their
 * import of `withRouter` / `RouteComponentProps`.
 */
export type RouteComponentProps<Params extends { [K in keyof Params]?: string } = {}> = {
  history: {
    push: (to: string, state?: unknown) => void
    replace: (to: string, state?: unknown) => void
    goBack: () => void
    goForward: () => void
    go: (n: number) => void
    location: ReturnType<typeof useLocation>
  }
  location: ReturnType<typeof useLocation>
  match: {
    params: Params
    path: string
    url: string
    isExact: boolean
  }
}

function makeHistoryShim(
  navigate: NavigateFunction,
  location: ReturnType<typeof useLocation>,
) {
  return {
    push: (to: string, state?: unknown) => navigate(to, { state }),
    replace: (to: string, state?: unknown) => navigate(to, { replace: true, state }),
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
    go: (n: number) => navigate(n),
    location,
  }
}

/**
 * v6-backed drop-in for react-router v5's `withRouter` HOC. Injects
 * `history` / `location` / `match` props via the v6 hooks underneath.
 */
export function withRouter<P extends Partial<RouteComponentProps>>(
  Wrapped: React.ComponentType<P>,
): React.FC<Omit<P, keyof RouteComponentProps>> {
  const WithRouterShim: React.FC<Omit<P, keyof RouteComponentProps>> = (props) => {
    const navigate = useNavigate()
    const location = useLocation()
    const params = useParams()
    const history = React.useMemo(() => makeHistoryShim(navigate, location), [navigate, location])
    const match = React.useMemo(
      () => ({
        params: params as P extends RouteComponentProps<infer Q> ? Q : {},
        path: location.pathname,
        url: location.pathname,
        isExact: true,
      }),
      [location.pathname, params],
    )

    return React.createElement(Wrapped as any, {
      ...(props as P),
      history,
      location,
      match,
    })
  }
  WithRouterShim.displayName = `withRouter(${Wrapped?.displayName ?? Wrapped?.name ?? 'Component'})`
  return WithRouterShim
}
